import { ProductPayload, ProductResponse, QueryParams, ResponseFormat, TAG_TYPES } from 'types';
import { baseApi } from './index';
import { Product as T } from 'models';
import { getQueryParams } from 'utils';

const urlEndpoint = `${process.env.REACT_APP_BASE_URL}/products`;
const tagType = TAG_TYPES.Product;
const productApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllProducts: builder.query<
      {
        data: {
          rows: T[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query);
        if (query === '') return { url: urlEndpoint, method: 'GET' };
        return {
          url: `${urlEndpoint}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [...(result?.data.rows || []).map(({ id }) => ({ type: tagType, id })), { type: tagType, id: 'LIST' }]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),

    getByIdProduct: builder.query<ProductResponse, { productId: string; versionId: string; query?: QueryParams }>({
      query: ({ productId, versionId, query }) => {
        const queryString = query ? `?${getQueryParams(query)}` : '';
        return {
          url: `${urlEndpoint}/${productId}/versions/${versionId}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { productId }) => [{ type: tagType, productId }],
    }),

    createProduct: builder.mutation<ProductResponse, ProductPayload>({
      query: (data) => ({
        url: urlEndpoint,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: tagType, id: 'LIST' }],
    }),
    createProductVersion: builder.mutation<ProductResponse, { productId: string; data: ProductPayload }>({
      query: ({ productId, data }) => ({
        url: `${urlEndpoint}/${productId}/versions/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: tagType, id: productId },
        { type: tagType, id: 'LIST' },
      ],
    }),

    updateProductVersion: builder.mutation<
      ProductResponse,
      { productId: string; versionId: string; data: Partial<ProductPayload> }
    >({
      query: ({ productId, versionId, data }) => ({
        url: `${urlEndpoint}/${productId}/versions/${versionId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: tagType, id: productId },
        { type: tagType, id: 'LIST' },
      ],
    }),

    deleteProduct: builder.mutation<ResponseFormat<T>, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),
  }),
});
export const {
  useGetAllProductsQuery: useGetProductsQuery,
  useLazyGetAllProductsQuery,
  useGetByIdProductQuery: useGetProductByIdQuery,
  useCreateProductMutation: useCreateProductMutation,
  useUpdateProductVersionMutation: useUpdateProductVersionMutation,
  useDeleteProductMutation: useDeleteProductMutation,
  useCreateProductVersionMutation: useCreateProductVersionMutation,
  useLazyGetByIdProductQuery: useLazyGetProductByIdQuery,
} = productApiService;
